import React, { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { AlertTriangle, Check, ExternalLink } from "lucide-react";

const sel = "rounded-lg border border-[#E3DED4] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#45423C] min-w-[120px]";
const norm = (s) => (s || "").toUpperCase().replace(/[^A-ZΑ-Ω0-9 ]/g, "").trim();

// best-guess card: exact name, else shares a word (≥4 chars) with the payroll name
const guess = (it, employees) => {
  const exact = employees.find((e) => norm(e.name) === norm(it.name));
  if (exact) return exact;
  const toks = norm(it.name).split(" ").filter((t) => t.length >= 4);
  return employees.find((e) => norm(e.name).split(" ").some((t) => t.length >= 4 && toks.includes(t)));
};

function Row({ it, employees, departments, roles, onApply }) {
  const g = guess(it, employees);
  const [empId, setEmpId] = useState(g?.id || "");
  const [dept, setDept] = useState(it.department || g?.department || "");
  const [pos, setPos] = useState(it.position || g?.position || "");
  const qc = useQueryClient();
  const id = it.code ?? it.name;
  const save = useMutation({
    mutationFn: async () => {
      const emp = employees.find((e) => e.id === empId);
      if (emp) await api.put(`/employees/${emp.id}`, { ...emp, department: dept, position: pos || dept });
      return !!emp;
    },
    onSuccess: (card) => {
      onApply(it.name, dept, pos || dept);
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success(`${it.name}: ${card ? "καρτέλα & " : ""}γραμμή μισθοδοσίας ενημερώθηκαν — πατήστε Αποθήκευση`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`missing-dept-row-${id}`}>
      <span className="text-sm font-medium text-[#45423C] min-w-[200px]">{it.name}</span>
      <select data-testid={`missing-dept-emp-${id}`} className={sel} value={empId} onChange={(e) => {
        const emp = employees.find((x) => x.id === e.target.value);
        setEmpId(e.target.value);
        if (emp?.department) setDept(emp.department);
        if (emp?.position) setPos(emp.position);
      }}>
        <option value="">— χωρίς καρτέλα —</option>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.department ? ` · ${e.department}` : ""}</option>)}
      </select>
      <input list="missing-dept-list" data-testid={`missing-dept-input-${id}`} className={sel} placeholder="Τμήμα"
        value={dept} onChange={(e) => setDept(e.target.value)} />
      <input list="missing-role-list" data-testid={`missing-role-input-${id}`} className={sel} placeholder="Ρόλος / Θέση"
        value={pos} onChange={(e) => setPos(e.target.value)} />
      <button data-testid={`missing-dept-save-${id}`} onClick={() => save.mutate()} disabled={save.isPending || !dept.trim()}
        className="inline-flex items-center gap-1 rounded-lg bg-[#6E7A63] text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50">
        <Check className="h-3.5 w-3.5" /> Εφαρμογή
      </button>
      <Link to="/employees" className="inline-flex items-center gap-1 text-xs text-[#C86235] hover:underline" data-testid={`missing-dept-card-${id}`}>
        Καρτέλα <ExternalLink className="h-3 w-3" />
      </Link>
      <datalist id="missing-dept-list">{departments.map((d) => <option key={d} value={d} />)}</datalist>
      <datalist id="missing-role-list">{roles.map((d) => <option key={d} value={d} />)}</datalist>
    </div>
  );
}

// Warns about payroll line items without department/role; quick fill updates the staff card + the run line.
export const MissingDeptFix = ({ items, employees, onApply }) => {
  const { data: org } = useQuery({ queryKey: ["org-settings"], queryFn: async () => (await api.get("/settings/org")).data });
  const missing = items.filter((it) => !it.department || !it.position);
  if (!missing.length) return null;
  const departments = Array.from(new Set([...(org?.departments || []), ...employees.map((e) => e.department).filter(Boolean)])).sort();
  const roles = Array.from(new Set(employees.map((e) => e.position).filter(Boolean))).sort();
  return (
    <div className="rounded-2xl border border-[#F0C36D] bg-[#FFFBEB] p-4 space-y-3 no-print" data-testid="missing-dept-warning">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#92400E]">
        <AlertTriangle className="h-4 w-4" /> {missing.length} υπάλληλοι χωρίς τμήμα ή ρόλο — επιλέξτε καρτέλα & συμπληρώστε για σωστή ταξινόμηση και υποσύνολα
      </div>
      <div className="space-y-2">
        {missing.map((it) => <Row key={it.name} it={it} employees={employees} departments={departments} roles={roles} onApply={onApply} />)}
      </div>
    </div>
  );
};
